/* =========================================================
   Sanford India — custom wishlist (localStorage based)
   ---------------------------------------------------------
   - Stores an array of product handles in localStorage
   - Toggles button state on product pages / cards
   - Renders the saved products on the wishlist page by
     fetching each product's JSON from /products/{handle}.js
   No app, no backend, no login required.
   ========================================================= */
(function () {
  'use strict';

  var STORAGE_KEY = 'sanford_wishlist_v1';

  /* ---------- storage helpers ---------- */
  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* storage full or blocked — fail silently */
    }
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', { detail: { count: list.length } })
    );
  }

  function has(handle) {
    return read().indexOf(handle) !== -1;
  }

  function toggle(handle) {
    var list = read();
    var i = list.indexOf(handle);
    if (i === -1) {
      list.push(handle);
    } else {
      list.splice(i, 1);
    }
    write(list);
    return list.indexOf(handle) !== -1; // true = now saved
  }

  function remove(handle) {
    var list = read();
    var i = list.indexOf(handle);
    if (i !== -1) {
      list.splice(i, 1);
      write(list);
    }
  }

  /* ---------- button state ---------- */
  function paintButton(btn) {
    var handle = btn.getAttribute('data-product-handle');
    var saved = has(handle);
    btn.classList.toggle('is-saved', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');

    var addLabel = btn.querySelector('[data-wishlist-label-add]');
    var addedLabel = btn.querySelector('[data-wishlist-label-added]');
    if (addLabel && addedLabel) {
      addLabel.hidden = saved;
      addedLabel.hidden = !saved;
    }
  }

  function paintAllButtons() {
    document
      .querySelectorAll('[data-wishlist-toggle]')
      .forEach(paintButton);
  }

  function bindButtons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      var handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      toggle(handle);
      paintButton(btn);
    });
  }

  /* ---------- header count badge (optional) ---------- */
  function paintCount() {
    var count = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /* ---------- wishlist page rendering ---------- */
  function formatMoney(cents, currency) {
    // Shopify product .js returns prices in cents.
    var amount = (cents / 100).toFixed(2);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'INR'
      }).format(cents / 100);
    } catch (e) {
      return amount;
    }
  }

  function cardHTML(product) {
    var img = product.featured_image
      ? product.featured_image
      : (product.images && product.images[0]) || '';
    var price = formatMoney(product.price, window.Shopify && window.Shopify.currency && window.Shopify.currency.active);

    return (
      '<div class="wishlist-card" data-wishlist-card="' + product.handle + '">' +
        '<a class="wishlist-card__media" href="/products/' + product.handle + '">' +
          (img ? '<img src="' + img + '" alt="" loading="lazy">' : '') +
        '</a>' +
        '<div class="wishlist-card__body">' +
          '<a class="wishlist-card__title" href="/products/' + product.handle + '">' +
            product.title +
          '</a>' +
          '<div class="wishlist-card__price">' + price + '</div>' +
          '<div class="wishlist-card__actions">' +
            '<a class="wishlist-card__view" href="/products/' + product.handle + '">View product</a>' +
            '<button type="button" class="wishlist-card__remove" data-wishlist-remove="' + product.handle + '">Remove</button>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function renderWishlistPage() {
    var grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;

    var empty = document.querySelector('[data-wishlist-empty]');
    var handles = read();

    function refreshEmptyState() {
      var current = read();
      if (empty) empty.hidden = current.length !== 0;
    }

    if (handles.length === 0) {
      grid.innerHTML = '';
      refreshEmptyState();
      return;
    }

    if (empty) empty.hidden = true;
    grid.innerHTML = '<p class="wishlist-loading">Loading your saved items…</p>';

    Promise.all(
      handles.map(function (handle) {
        return fetch('/products/' + handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      })
    ).then(function (products) {
      var valid = products.filter(Boolean);

      // Drop any handles that no longer resolve (unpublished/deleted).
      var validHandles = valid.map(function (p) { return p.handle; });
      if (validHandles.length !== handles.length) {
        write(validHandles);
      }

      if (valid.length === 0) {
        grid.innerHTML = '';
        refreshEmptyState();
        return;
      }

      grid.innerHTML = valid.map(cardHTML).join('');
    });

    // Handle "Remove" clicks on the page.
    grid.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-remove]');
      if (!btn) return;
      var handle = btn.getAttribute('data-wishlist-remove');
      remove(handle);
      var card = grid.querySelector('[data-wishlist-card="' + handle + '"]');
      if (card) card.remove();
      refreshEmptyState();
    });
  }

  /* ---------- init ---------- */
  function init() {
    bindButtons();
    paintAllButtons();
    paintCount();
    renderWishlistPage();

    document.addEventListener('wishlist:updated', function () {
      paintAllButtons();
      paintCount();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
